/* eslint-disable react-refresh/only-export-components */

import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { AlertTriangle, Copy, Pencil, Zap } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { bulkCloneRiskRules } from '@/api/risk';
import type { RuleTemplateRule } from '@/api/risk/types';
import { HelpSheet } from '@/components/shared/HelpSheet';
import { PageContainer } from '@/components/shared/PageContainer';
import {
  PageHeaderCard,
  PageHeaderIdentity,
  PageHeaderMeta,
  PageHeaderTopRow,
} from '@/components/shared/PageHeaderCard';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useTranslator } from '@/hooks/useTranslator';
import { cn } from '@/lib/utils/cn';
import { useSessionStore } from '@/store/sessionStore';
import { RuleCard } from '../../../../-components/builder/RuleCard';
import { TagsField } from '../../../../-components/builder/TagsField';
import { TemplateHeaderCard } from '../../../../-components/builder/TemplateHeaderCard';
import { useAllRisks } from '../../../../-hooks/useAllRisks';
import { useRuleTemplate } from '../../../../-hooks/useRuleTemplate';
import { useToggleActiveRuleTemplate } from '../../../../-hooks/useToggleActiveRuleTemplate';

export const Route = createFileRoute(
  '/(client)/risk/(pages)/(detail)/rules/$id/',
)({
  component: RuleDetailPage,
});

function RuleDetailPage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { template, metrics } = useRuleTemplate(id);
  const { getTranslation: t } = useTranslator({ page: 'riskRules' });

  const { data: allRisks = [] } = useAllRisks();
  const riskOptions = allRisks.map(r => ({ key: r.id, label: r.title }));
  const [rules, setRules] = useState<RuleTemplateRule[]>([]);
  const [helpOpen, setHelpOpen] = useState(false);
  const d = template.data;

  // Mirror the rules-table rule: editable only when I own it AND it isn't
  // published. Retraced presets / others' templates / published → no edit.
  const myCompanyId = useSessionStore(s => s.user?.companyId ?? '');
  const canEdit =
    d?.createdByCompanyId === myCompanyId && d?.status !== 'PUBLISHED';
  const activateMutation = useToggleActiveRuleTemplate(
    id,
    d
      ? {
          title: d.title,
          ...(d.description != null ? { description: d.description } : {}),
          tags: d.tags ?? [],
          ...(d.defaultSeverity != null
            ? { defaultSeverity: d.defaultSeverity }
            : {}),
          ...(d.scoreFormula != null ? { scoreFormula: d.scoreFormula } : {}),
          visibility: (d.visibility as 'PRIVATE' | 'PUBLIC') ?? 'PRIVATE',
        }
      : undefined,
    t('detail.activateSuccess'),
  );

  useEffect(() => {
    if (!d) return;
    setRules(d.rules?.length ? d.rules : []);
  }, [d]);

  const handleClone = async () => {
    try {
      await bulkCloneRiskRules([id]);
      await queryClient.invalidateQueries({ queryKey: ['risk', 'rules'] });
      toast.success(t('detail.cloneSuccess'));
      void navigate({ to: '/risk/rules' });
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  if (template.isLoading) {
    return (
      <PageContainer className='gap-4'>
        {[1, 2, 3].map(i => (
          <Skeleton key={i} className='h-32 w-full' />
        ))}
      </PageContainer>
    );
  }

  const statusLabel =
    d?.status === 'PUBLISHED'
      ? t('builder.status.published')
      : t('builder.status.draft');

  return (
    <div className='@container flex flex-col h-full overflow-hidden'>
      <PageContainer className='overflow-y-auto'>
        {d && (
          <div className='flex flex-col gap-6'>
            {/* Full-width page header — the template's identity + CTAs, like the
                review / answer page header above the centered content. */}
            <PageHeaderCard>
              <PageHeaderTopRow>
                <PageHeaderIdentity
                  avatar={
                    <div className='w-11 h-11 rounded-xl grid place-items-center bg-primary/10 text-primary'>
                      <AlertTriangle className='size-5' />
                    </div>
                  }
                  title={d.title || '—'}
                  titleAccessory={
                    <span
                      className={cn(
                        'inline-flex items-center gap-1.5 h-6 px-2.5 rounded-full text-xs font-semibold shrink-0',
                        d.status === 'PUBLISHED'
                          ? 'bg-success/15 text-success'
                          : 'bg-warning/15 text-warning',
                      )}
                    >
                      <span
                        className={cn(
                          'size-1.5 rounded-full',
                          d.status === 'PUBLISHED'
                            ? 'bg-success'
                            : 'bg-warning',
                        )}
                      />
                      {statusLabel}
                    </span>
                  }
                  subtitle={d.description || t('detail.subtitle')}
                  badges={
                    d.tags?.length ? (
                      <TagsField tags={d.tags} isReadOnly />
                    ) : undefined
                  }
                />
              </PageHeaderTopRow>
              <PageHeaderMeta
                items={[]}
                action={
                  <div className='flex items-center gap-1.5'>
                    {!d.isActive && d.createdByCompanyId === myCompanyId && (
                      <AlertDialog>
                        <AlertDialogTrigger
                          render={
                            <Button
                              type='button'
                              variant='outline'
                              className='gap-1.5'
                              disabled={activateMutation.isPending}
                            />
                          }
                        >
                          <Zap className='size-3.5' />
                          {t('detail.activate')}
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>
                              {t('detail.activateDialog.title')}
                            </AlertDialogTitle>
                            <AlertDialogDescription>
                              {t('detail.activateDialog.description')}
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <div className='flex justify-end gap-2 pt-2'>
                            <AlertDialogCancel>
                              {t('detail.activateDialog.cancel')}
                            </AlertDialogCancel>
                            <AlertDialogAction
                              variant='default'
                              onClick={() => activateMutation.mutate(1)}
                            >
                              {t('detail.activateDialog.confirm')}
                            </AlertDialogAction>
                          </div>
                        </AlertDialogContent>
                      </AlertDialog>
                    )}
                    <Button
                      type='button'
                      variant='ghost'
                      onClick={handleClone}
                      className='gap-1.5'
                    >
                      <Copy className='size-3.5' />
                      {t('list.header.clone')}
                    </Button>
                    {canEdit && (
                      <Button
                        type='button'
                        variant='outline'
                        className='gap-1.5'
                        onClick={() =>
                          void navigate({
                            to: '/risk/rules/$id/edit',
                            params: { id },
                          })
                        }
                      >
                        <Pencil className='size-3.5' />
                        {t('list.editButton')}
                      </Button>
                    )}
                    <Button
                      type='button'
                      variant='ghost'
                      className='size-9 p-0'
                      onClick={() => setHelpOpen(true)}
                    >
                      <i className='fa-regular fa-circle-question text-sm' />
                    </Button>
                  </div>
                }
              />
            </PageHeaderCard>

            {/* Centered column (max-w-5xl): the template settings card followed
                by the rule cards, like the review section content. */}
            <div className='mx-auto w-full max-w-5xl flex flex-col gap-5'>
              <TemplateHeaderCard
                key={d.id}
                title={d.title}
                description={d.description ?? ''}
                tags={d.tags ?? []}
                defaultSeverity={d.defaultSeverity ?? 3}
                scoreFormula={d.scoreFormula ?? ''}
                status={d.status}
                // "Visibility" (Visible/Hidden) mirrors v1: it controls isActive —
                // i.e. whether the rule appears in the assessment template dropdown.
                visibility={d.isActive ? 'PUBLIC' : 'PRIVATE'}
                metrics={metrics}
                isReadOnly
                hideIdentity
                // Editable only for an owned, published rule — matching v1, where the
                // visibility switch appears only after publish. Drafts use Activate.
                {...(d.createdByCompanyId === myCompanyId &&
                d.status === 'PUBLISHED'
                  ? {
                      onVisibilityChange: (v: 'PRIVATE' | 'PUBLIC') =>
                        activateMutation.mutate(v === 'PUBLIC' ? 1 : 0),
                    }
                  : {})}
              />

              <div className='flex items-center gap-2.5'>
                <h2 className='text-sm font-semibold tracking-tight'>
                  {t('detail.rules')}
                </h2>
                <span className='inline-flex items-center h-5 px-2 rounded-full bg-secondary text-secondary-foreground text-xs font-semibold tabular-nums'>
                  {rules.length}
                </span>
              </div>

              {rules.map((rule, i) => (
                <RuleCard
                  key={i}
                  rule={rule}
                  index={i}
                  risks={riskOptions}
                  isReadOnly
                />
              ))}
            </div>
          </div>
        )}
      </PageContainer>

      <HelpSheet
        articleId='8960666'
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
      />
    </div>
  );
}
